import {
	addPeriod,
	getCapitalCase,
	getCapitalCaseAll,
	getNumberOfNightsBetweenDates,
} from '../../utils';
import { completePreCheckin, fetchStage } from '../../redux/slices/stageSlice';
import {
	fetchRetryStatus,
	selectProfile,
	setCurrentProfile,
} from '../../redux/slices/profileSlice';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from './../../redux/hook';

import { bypassConfirmLeaveOnce } from '../../hooks/confirmLeavePageBypass';
import { DarkButton } from '../../components/dark-button';
import { DarkInfoCard } from '../../components/dark-info-card';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import format from 'date-fns/format';
import FullPageLoader from '../../components/loader';
import { getCurrentStage } from '../../redux/api';
import GuestProfiles from './components/guest-profiles';
import QRCode from 'react-qr-code';
import { selectSession } from '../../redux/slices/sessionSlice';
import { selectStage } from '../../redux/slices/stageSlice';
import SkipModal from '../../app/reservation-profile-form/components/skip-modal';
import { startVerificationThunk } from '../../redux/slices/verification.slice';
import styles from '../reservation-profile-form/page.module.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

//import TakeSelfieModal from '../det/takeselfie-modal/takeselfie-modal';

interface ReservationProfileFormHomeProps {
	setGuestWithMe?: Dispatch<SetStateAction<boolean | null>>;
	onBack?: () => void;
}
export default function ReservationProfileFormHome({
	setGuestWithMe,
	onBack,
}: ReservationProfileFormHomeProps) {
	const [complete, setComplete] = useState(false);
	const [isMobile, setIsMobile] = useState(true);
	const [currentLink, setCurrentLink] = useState('');
	const [showSkipModal, setShowSkipModal] = useState(false);
	//const [showTakeSelfieModal, setShowTakeSelfieModal] = useState(false);
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const {
		token,
		reservation,
		propertyInfo,
		settings,
		profiles,
		isPreCheckinDone,
	} = useAppSelector(selectSession);

	const propertyLogoUrl =
		propertyInfo?.logo ||
		propertyInfo?.logoUrl ||
		propertyInfo?.propertyLogo ||
		'';
	const { currentProfile } = useAppSelector(selectProfile);
	const { stages, stage } = useAppSelector(selectStage);

	const isPageReady = token && propertyInfo && reservation && profiles;
	const enableDocSubmission = settings?.find(
		(setting: any) =>
			setting.parameterName === 'EnableDocSubmission' &&
			setting.parameterValue === 'false'
	);
	const safeFormatDate = (
		value?: string | Date | null,
		pattern = 'dd/MMM/yyyy'
	) => {
		if (!value) return '';

		const d = value instanceof Date ? value : new Date(value);

		if (isNaN(d.getTime())) return '';

		return format(d, pattern);
	};
	const [showGuestPopup, setShowGuestPopup] = useState(false);

	const [selectedProfile, setSelectedProfile] = useState<any>(null);

	//const [guestWithMe, setGuestWithMe] = useState<boolean | null>(null);
	const [showManualCheckinFrontDesk, setShowManualCheckinFrontDesk] =
		useState(false);
	const [verifyingProfiles, setVerifyingProfiles] = useState<Set<number>>(
		new Set()
	);
	const isVerifiedStatus = (status?: string | null) => {
		const normalized = status?.toLowerCase();
		return (
			normalized === 'completed' ||
			normalized === 'verified' ||
			normalized === 'shared'
		);
	};
	const allProfilesVerified =
		!!profiles?.length &&
		profiles.every((p: any) => isVerifiedStatus(p.verificationStatus));

	const hasPendingVerificationProfiles =
		!!profiles?.length &&
		profiles.some((p: any) => !isVerifiedStatus(p.verificationStatus));

	const handleBack = () => {
		if (hasPendingVerificationProfiles || !allProfilesVerified) {
			Swal.fire({
				title: t('Verification Incomplete') || 'Verification Incomplete',
				text:
					t('All verifications must be completed before going back.') ||
					'All verifications must be completed before going back.',
				icon: 'warning',
				confirmButtonText: t('OK') || 'OK',
			});
			return;
		}

		if (onBack) {
			onBack();
		} else {
			bypassConfirmLeaveOnce();
			window.history.back();
		}
	};

	useEffect(() => {
		const handlePopState = () => {
			if (hasPendingVerificationProfiles || !allProfilesVerified) {
				window.history.pushState(null, '', window.location.href);
				Swal.fire({
					title: t('Verification Incomplete') || 'Verification Incomplete',
					text:
						t('All verifications must be completed before going back.') ||
						'All verifications must be completed before going back.',
					icon: 'warning',
					confirmButtonText: t('OK') || 'OK',
				});
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [hasPendingVerificationProfiles, allProfilesVerified, t]);

	const isPrimaryVerified = profiles?.some(
		(p: any) => p.isPrimary && isVerifiedStatus(p.verificationStatus)
	);
	const primaryProfile = profiles?.find((p: any) => p.isPrimary);

	const hasCompletedStageInHistory = (reservationHistory: any[]) => {
		return reservationHistory.some((historyItem: any) => {
			return historyItem?.isComplete === true;
		});
	};

	const isProfileEligibleForDirectPrecheckin = (profile: any) => {
		return (
			profile?.isPrimary ||
			profile?.isChild ||
			profile?.isGuestForPrecheckin === true
		);
	};

	const getReservationHistoryForProfile = async (profile: any) => {
		const profileHistory =
			profile?.reservationHistory ??
			profile?.reservationStageHistory ??
			profile?.history;
		if (Array.isArray(profileHistory)) return profileHistory;
		if (!profile?.profileID || profile.profileID <= 0) return [];
		if (!token || !reservation?.PmsConfirmationNo) return [];

		try {
			const response = await getCurrentStage({
				token,
				type: process.env.REACT_APP_TYPE!,
				ConfirmationNo: reservation.PmsConfirmationNo,
				propertyCode: propertyInfo?.propertyCode ?? null,
				profileID: profile.profileID,
			});
			return response?.data?.data?.reservationStageHistory ?? [];
		} catch (error) {
			console.error('Failed to fetch reservation history for profile:', error);
			return [];
		}
	};

	const onVerifyGuest = async (profile: any) => {
		console.log('Verify clicked:', profile);

		setVerifyingProfiles((prev) => new Set(prev).add(profile.profileID));

		const updatedProfile = {
			...(profile ?? {}),
			reservationID: reservation?.ReservationID,
			pmsConfirmationNo: reservation?.PmsConfirmationNo,
		};

		try {
			const res: any = await dispatch(
				fetchRetryStatus({
					token,
					sessionToken: token,
					profileID: updatedProfile?.profileID ?? null,
				})
			).unwrap();

			if (res?.result === true) {
				console.log(
					'Retry count indicates document upload failure, showing manual check-in option for front desk'
				);
				setVerifyingProfiles((prev) => {
					const newSet = new Set(prev);
					newSet.delete(profile.profileID);
					return newSet;
				});
				setShowManualCheckinFrontDesk(true);
				return;
			} else {
				if (isProfileEligibleForDirectPrecheckin(profile)) {
					if (profile?.isGuestForPrecheckin === true) {
						setGuestWithMe?.(true);
					}
					// Primary/child guest -> go directly
					startVerification(profile);
					return;
				}

				const reservationHistory = await getReservationHistoryForProfile(
					profile
				);
				const hasCompletedStage =
					hasCompletedStageInHistory(reservationHistory);
				if (hasCompletedStage) {
					// Reservation has completed stage -> resume directly.
					startVerification(profile);
					return;
				}

				// No completed stage -> always show popup.
				setSelectedProfile(profile);
				setShowGuestPopup(true);
				setVerifyingProfiles((prev) => {
					const newSet = new Set(prev);
					newSet.delete(profile.profileID);
					return newSet;
				});
			}
		} catch (err: any) {
			console.error('Failed to fetch retry status', err);
			setVerifyingProfiles((prev) => {
				const newSet = new Set(prev);
				newSet.delete(profile.profileID);
				return newSet;
			});
			return;
		}
	};

	const completeInitialStage = () => {
		console.log('---------------------------------------------stages', stages);
		console.log('---------------------------------------------stage', stage);
		console.log(
			'---------------------------------------------stage to complete',
			stage?.stepID
		);
		dispatch(
			completePreCheckin({
				token: token,
				confirmationNo: reservation?.PmsConfirmationNo,
				profileId: currentProfile?.profileID,
				type: 'checkin',
			})
		)
			.unwrap()
			.then(() => {
				proceedToThankYou();
			})
			.catch((error) => {
				console.error('Failed to complete pre-checkin:', error);
				// Optionally show an error message to the user here
				// alert('Failed to complete pre-checkin. Please try again.');
			});
	};

	const proceedToThankYou = () => {
		const profileToUse = currentProfile ?? primaryProfile;
		if (
			!token ||
			!reservation?.PmsConfirmationNo ||
			!stages ||
			!profileToUse?.profileID
		) {
			return;
		}

		dispatch(setCurrentProfile(profileToUse));
		dispatch(
			fetchStage({
				token,
				type: process.env.REACT_APP_TYPE!,
				confirmationNo: reservation.PmsConfirmationNo,
				stages,
				profileID: profileToUse.profileID,
			})
		);
	};

	const startVerification = (profile: any) => {
		const confirmationNo = reservation?.PmsConfirmationNo;

		if (!confirmationNo) return;
		const updatedProfile = {
			...(profile ?? {}),
			reservationID: reservation?.ReservationID,
			pmsConfirmationNo: reservation?.PmsConfirmationNo,
		};

		setVerifyingProfiles((prev) => {
			const newSet = new Set(prev);
			newSet.delete(updatedProfile.profileID);
			return newSet;
		});

		console.log('Starting verification for profile:', updatedProfile);
		dispatch(
			startVerificationThunk({
				token,
				profile: updatedProfile,
				stages,
				type: 'checkin',
				confirmationNo: confirmationNo,
			})
		);
	};

	const htmlDecode: (input: string) => string | TrustedHTML = (
		input: string
	) => {
		const e = document.createElement('p');
		e.innerHTML = input;
		const html = e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
		console.log('html', html);
		console.log('input', input);
		console.log('e', e);
		return input;
	};

	// const initializeIsDesktop = async (onboardtoken: string) => {
	// 	const baseUrl = process.env.REACT_APP_API_BACKEND_BASE_URL;

	// 	if (!baseUrl) {
	// 		console.error('REACT_APP_API_BACKEND_BASE_URL is missing');
	// 		return false;
	// 	}

	// 	const incodeClient = create({
	// 		apiURL: `${baseUrl}/incode/proxy/${encodeURIComponent(
	// 			onboardtoken ?? ''
	// 		)}`,
	// 		customHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
	// 	});

	// 	if (incodeClient.isDesktop()) {
	// 		alert(
	// 			'Desktop environment detected. Please use a mobile device for guest verification.'
	// 		);
	// 		setIsMobile(false);
	// 	} else {
	// 		alert(
	// 			'Mobile environment detected. You can proceed with guest verification.'
	// 		);
	// 		setIsMobile(true);
	// 	}
	// };
	const detectDevice = () => {
		const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(
			navigator.userAgent
		);

		if (isMobileDevice) {
			setIsMobile(true);
		} else {
			setIsMobile(false);
		}
	};

	// useEffect(() => {
	// 	const primary = profiles?.find((p: any) => p.isPrimary);

	// 	if (token && primary && reservation?.PmsConfirmationNo && stages?.length) {
	// 		dispatch(
	// 			fetchStage({
	// 				token,
	// 				type: process.env.REACT_APP_TYPE!,
	// 				confirmationNo: reservation.PmsConfirmationNo,
	// 				stages,
	// 				profileID: primary.profileID,
	// 			})
	// 		);
	// 	}
	// }, [profiles, reservation?.PmsConfirmationNo]);

	useEffect(() => {
		detectDevice();
		// const checkMobile = () => {
		// 	setIsMobile(isMobileDevice());
		// };

		// checkMobile(); // initial check

		// window.addEventListener('resize', checkMobile);

		// return () => {
		// 	window.removeEventListener('resize', checkMobile);
		// };
	}, []);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setCurrentLink(window.location.href);
		}
	}, []);

	// useEffect(() => {
	// 	const primary = profiles?.find((p: any) => p.isPrimary);

	// 	if (primary && stages && reservation?.PmsConfirmationNo) {
	// 		dispatch(
	// 			fetchStage({
	// 				token,
	// 				type: process.env.REACT_APP_TYPE!,
	// 				confirmationNo: reservation.PmsConfirmationNo,
	// 				stages,
	// 				profileID: primary.profileID,
	// 			})
	// 		);
	// 	}
	// }, [profiles, stages, reservation?.PmsConfirmationNo]);

	useEffect(() => {
		if (
			currentProfile?.profileID > 0 &&
			stages &&
			reservation?.PmsConfirmationNo
		) {
			dispatch(
				fetchStage({
					token,
					type: process.env.REACT_APP_TYPE!,
					confirmationNo: reservation.PmsConfirmationNo,
					stages,
					profileID: currentProfile.profileID,
				})
			);
		}
	}, [
		currentProfile?.profileID, // key trigger
		stages,
		reservation?.PmsConfirmationNo,
	]);
	if (isMobile && !isPageReady) {
		return <FullPageLoader />;
	}
	return (
		<>
			{!isMobile && (
				<>
					<div className="min-vh-100 d-flex align-items-center justify-content-center px-3 bg-light">
						<div
							className="bg-white rounded-4 shadow-sm p-4 text-center"
							style={{ maxWidth: '420px', width: '100%' }}>
							{/* ICON */}
							<div className="mb-3">
								<span style={{ fontSize: '42px' }}>📱</span>
							</div>

							{/* TITLE */}
							<h5 className="fw-bold mb-2">Mobile device required</h5>

							{/* DESCRIPTION */}
							<p className="text-muted mb-3">
								This experience is designed for mobile phones to ensure secure
								and smooth guest verification.
							</p>

							{/* INSTRUCTION */}
							<div className="border rounded-3 p-3 bg-light">
								<small className="text-muted d-block">
									Please open this link on your mobile phone to continue.
								</small>
								{currentLink && (
									<div className="d-flex flex-column align-items-center gap-2 mt-3">
										<div className="bg-white p-2 rounded">
											<QRCode value={currentLink} size={170} />
										</div>
										<small className="text-muted">
											Scan this QR code from your mobile device.
										</small>
										{/* <a
											href={currentLink}
											className="small text-break"
											target="_blank"
											rel="noreferrer">
											click here to open on mobile
										</a> */}
									</div>
								)}
							</div>
						</div>
					</div>
				</>
			)}

			{isMobile && (
				<>
					{/* MAIN SCROLLABLE CONTAINER */}
					<div
						className="d-flex flex-column gap-2 min-vh-100 mx-auto"
						style={{
							paddingBottom: '140px',
							overflowY: 'auto',
						}}>
						{/* HEADER */}
						<div className={`position-relative ${styles.property_header}`}>
							<img
								src={
									propertyLogoUrl && propertyLogoUrl.length > 0
										? propertyLogoUrl
										: process.env.PUBLIC_URL + '/assets/icons/logo-hd.png'
								}
								alt="Property"
								className={styles.property_icon}
							/>
						</div>

						{/* CONTENT */}
						<div className="px-3 px-md-2 d-flex flex-column gap-4">
							{/* GUEST NAME */}
							<div className="text-center">
								{/* Welcome line */}
								<p className="text-muted mb-1">{t('LandingPageWelcome')}</p>
								{/* Guest name */}
								<h2 className="fs-5 fs-md-4 mb-2">
									{getCapitalCase(
										addPeriod(reservation?.Title ?? '') +
											' ' +
											(reservation?.FirstName ?? '') +
											' ' +
											(reservation?.LastName ?? '')
									)}
								</h2>

								{/* <p className={styles.welcome_message}>
									{t('LandingPageWelcomeMessage')}
								</p> */}
							</div>

							{/* RESERVATION INFO */}
							<DarkInfoCard
								confirmationNumber={reservation?.PmsConfirmationNo}
								cardFields={[
									// {
									// 	title: t('LandingPagePropertyName'),
									// 	description: propertyInfo?.property ?? '',
									// 	textStyle: 'true',
									// 	span: 12,
									// },
									{
										title: t('LandingPageArrivalDate'),
										description: getCapitalCaseAll(
											safeFormatDate(reservation?.ArrivalDate, 'dd/MMM/yyyy')
										)!,
										textStyle: 'true',
										span: 6,
									},
									{
										title: t('LandingPageDepartureDate'),
										description: getCapitalCaseAll(
											safeFormatDate(reservation?.DepartureDate, 'dd/MMM/yyyy')
										)!,
										textStyle: 'true',

										span: 6,
									},
									{
										title:
											t('LandingPageAdult') + '/' + t('LandingPageChildren'),
										description: `${reservation?.AdultCount}/${
											reservation?.ChildCount ?? 0
										}`,
										textStyle: 'true',
										span: 6,
									},
									{
										title: t('LandingPageNumberOfNights'),
										description: getNumberOfNightsBetweenDates(
											new Date(reservation?.ArrivalDate),
											new Date(reservation?.DepartureDate)
										)!,
										textStyle: 'true',
										span: 6,
									},
									{
										title: t('LandingPageRoomType'),
										description: getCapitalCase(
											reservation?.RoomTypeName ?? reservation?.RateCode
										)!,
										textStyle: 'true',
										span: 12,
									},
								]}
							/>

							{/* VERIFY GUEST DETAILS */}
							<div className="d-flex flex-column gap-3">
								<h4 className="fw-bold">{t('LandingVerifyGuestTitle')}</h4>
								<small className={styles.guest_description}>
									{t('LandingPageVerifyGuestDescription')}
								</small>

								{/* {GuestProfiles(profiles, startVerification)} */}
								{/* {GuestProfiles(profiles, onVerifyGuest)} */}
								<GuestProfiles
									profiles={profiles}
									onVerifyGuest={onVerifyGuest}
									verifyingProfiles={verifyingProfiles}
								/>
							</div>

							{/* FOOTER TEXT */}
							{enableDocSubmission == null && (
								<small className="text-center text-muted pt-3">
									{/* {t('LandingPageFooterMessage')} */}
								</small>
							)}
						</div>
					</div>
					{/* FIXED FOOTER BUTTON */}
					{/*<div
						className="fixed-bottom w-100 px-3 px-md-5 py-3 border-top bg-white d-flex justify-content-center theme-footer"
						style={{
							paddingBottom: 'env(safe-area-inset-bottom)',
						}}>
						 {reservation?.PmsConfirmationNo && isPrimaryVerified && (
							<DarkButton
								text={
									allProfilesVerified
										? isPreCheckinDone
											? t('LandingPageCompletedPrecheckin')
											: t('LandingPageCompletePrecheckin') // all verified
										: t('LandingPageSkipButton') // some not verified
								}
								onClick={() => {
									if (isPreCheckinDone) {
										proceedToThankYou();
										return;
									}
									console.log('show skip modal');
									setShowSkipModal(true);
								}}
								hideArrow
								filled={allProfilesVerified}
								override={allProfilesVerified && isPreCheckinDone}
								className={
									allProfilesVerified && isPreCheckinDone
										? 'completed-precheckin-btn'
										: undefined
								}
							/>
						)} 
					</div>*/}
					{showGuestPopup && (
						<>
							{/* BACKDROP */}
							<div
								className={styles.bottomSheetBackdrop}
								onClick={() => setShowGuestPopup(false)}
							/>

							{/* BOTTOM SHEET */}
							<div className={styles.bottomSheet}>
								{/* HEADER */}
								<div className="d-flex justify-content-between align-items-start">
									<h5 className="fw-semibold mb-1">
										{t('LandingPageIsTheGuestWithYou')}
									</h5>
									<button
										className="btn-close"
										onClick={() => setShowGuestPopup(false)}
									/>
								</div>

								<p className="text-muted mt-2 mb-3">
									{t('LandingPageGuestWithYouDescription')}
								</p>

								{/* YES */}
								<button
									className={styles.sheetOption}
									disabled={verifyingProfiles.has(selectedProfile?.profileID)}
									onClick={() => {
										console.log('----------------Guest is with me');
										console.log('yes clicked');
										setShowGuestPopup(false);

										/* eslint-disable no-debugger */

										setGuestWithMe?.(true);
										// Mark stage 2 complete immediately for non-primary guest
										// if (stage?.stepID === 1 || stage?.stepCode === 'SEI01') {
										// 	dispatch(
										// 		completeStage({
										// 			token,
										// 			stageID: stage.stepID,
										// 			ReservationID: reservation?.ReservationID,
										// 			type: 'checkin',
										// 			confirmationNo: reservation?.PmsConfirmationNo,
										// 			stages,
										// 			propertyCode: propertyInfo?.propertyCode ?? '',
										// 			profileID:
										// 				selectedProfile?.profileID ??
										// 				currentProfile?.profileID,
										// 		})
										// 	);
										// }

										if (selectedProfile) startVerification(selectedProfile);
									}}>
									<div>
										<div className="fw-semibold">
											{verifyingProfiles.has(selectedProfile?.profileID) ? (
												<span
													className="spinner-border spinner-border-sm me-2"
													role="status"
													aria-hidden="true"></span>
											) : null}
											{t('LandingPageYesGuestWithMe')}
										</div>
										<small className="text-muted">
											{t('LandingPageVerifyNowUsingThisDevice')}
										</small>
									</div>
									{!verifyingProfiles.has(selectedProfile?.profileID) && (
										<FontAwesomeIcon icon={faChevronRight} />
									)}
								</button>

								{/* NO */}
								<button
									className={styles.sheetOption}
									disabled={verifyingProfiles.has(selectedProfile?.profileID)}
									onClick={() => {
										console.log('No clicked');
										setShowGuestPopup(false);
										setGuestWithMe?.(false);
										if (selectedProfile) startVerification(selectedProfile);
										console.log('Send verification link');
									}}>
									<div>
										<div className="fw-semibold">
											{verifyingProfiles.has(selectedProfile?.profileID) ? (
												<span
													className="spinner-border spinner-border-sm me-2"
													role="status"
													aria-hidden="true"></span>
											) : null}
											{t('LandingPageNoGuestNotWithMe')}
										</div>
										<small className="text-muted">
											{t('LandingPageSendVerificationLink')}
										</small>
									</div>
									{!verifyingProfiles.has(selectedProfile?.profileID) && (
										<FontAwesomeIcon icon={faChevronRight} />
									)}
								</button>
							</div>
						</>
					)}

					{showManualCheckinFrontDesk && (
						<>
							{/* BACKDROP */}
							<div
								className={styles.bottomSheetBackdrop}
								onClick={() => setShowManualCheckinFrontDesk(false)}
							/>

							{/* BOTTOM SHEET */}
							<div className={styles.bottomSheet}>
								{/* HEADER */}
								<div className="d-flex justify-content-between align-items-start">
									<h5 className="fw-semibold mb-1">
										{t('ManualCheckinTitle') || 'Manual Check-in Required'}
									</h5>
									<button
										className="btn-close"
										onClick={() => setShowManualCheckinFrontDesk(false)}
									/>
								</div>

								{/* CONTENT */}
								<p className="text-muted mt-3 mb-3">
									{t('ManualCheckinDescription') ||
										'Your pre-check-in was not completed successfully. Kindly visit the reception for further assistance.'}
								</p>

								{/* CONTACT FRONT DESK */}
								<button
									className={styles.sheetOption}
									onClick={() => {
										console.log('Contact front desk');
										setShowManualCheckinFrontDesk(false);
										// You can add logic here to navigate to a support page or show contact info
									}}>
									<div>
										<div className="fw-semibold">
											{t('ContactFrontDesk') || 'Contact Front Desk'}
										</div>
										<small className="text-muted">
											{t('FrontDeskAssistance') ||
												'Get immediate assistance from front desk staff'}
										</small>
									</div>
								</button>
							</div>
						</>
					)}

					<SkipModal
						show={showSkipModal}
						mode={allProfilesVerified ? 'complete' : 'skip'}
						onClose={() => setShowSkipModal(false)} // closes modal
						onUnderstand={() => {
							setShowSkipModal(false);
							completeInitialStage(); // moves to next steps like addons, reg card etc.
						}}
					/>
					{/* <TakeSelfieModal
						show={showTakeSelfieModal}
						onClose={() => setShowTakeSelfieModal(false)} // closes modal
						onUnderstand={() => {
							setShowTakeSelfieModal(false);
							completeInitialStage(); // moves to next steps like addons, reg card etc.
						}}
					/> */}
				</>
			)}
		</>
	);
}
